import { FormattedMessage, useIntl } from 'react-intl';

export interface HeroProps {
  onSignUpClick: () => void;
  onScrollToFeatures?: () => void;
}

/**
 * Hero V4c — port of the design-handoff `index.html` hero. Two perspective
 * browser windows (coach + athlete), sync-line SVG between them, mouse-follow
 * spotlight on the dark grid, and live set-ticking inside the athlete window.
 * The interactive bits are driven by `useLandingAnimations` (mounted once at
 * the page root in Home.tsx).
 *
 * User overrides vs handoff: no fake trust block ("240+ coaches activos · …")
 * and no "Ver demo · 2 min" CTA (we don't have a demo asset). The single CTA
 * goes to signup.
 */
const Hero = ({ onSignUpClick }: HeroProps) => {
  const intl = useIntl();

  return (
    <section className="hero" data-screen-label="01 Hero">
      <div className="ambient" />
      <div className="hero-body">
        {/* LEFT — COPY */}
        <div className="copy">
          <div className="eyebrow on-dark">
            <span className="eyebrow-dot" />
            <FormattedMessage id="landing.hero.eyebrow" defaultMessage="TEMPORADA 2026 · BETA ABIERTA" />
          </div>

          <h1>
            <FormattedMessage id="landing.hero.title.line1" defaultMessage="Todos tus atletas," />
            <br />
            <FormattedMessage id="landing.hero.title.line2.prefix" defaultMessage="en " />
            <span className="accent">
              <FormattedMessage id="landing.hero.title.line2.accent" defaultMessage="un solo lugar" />
            </span>
            <FormattedMessage id="landing.hero.title.line2.suffix" defaultMessage="." />
          </h1>

          <p className="lede">
            <FormattedMessage
              id="landing.hero.lede"
              defaultMessage="Una sola plataforma para planificar ciclos, asignar entrenamientos y medir progreso. Vos al control. Ellos enfocados en entrenar."
            />
          </p>

          <div className="ctas">
            <button className="btn-lime-lg" type="button" onClick={onSignUpClick}>
              {intl.formatMessage({ id: 'landing.hero.cta.start', defaultMessage: 'Comenzar gratis' })}
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M3 8h10M9 4l4 4-4 4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* RIGHT — STAGE: two browser windows + sync line */}
        <div className="stage" id="stage">
          <svg className="sync-svg" preserveAspectRatio="none">
            <defs>
              <linearGradient id="syncGrad" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#a3e635" stopOpacity="0.05" />
                <stop offset="50%" stopColor="#a3e635" stopOpacity="0.9" />
                <stop offset="100%" stopColor="#a3e635" stopOpacity="0.05" />
              </linearGradient>
            </defs>
            <path
              id="syncPath"
              stroke="url(#syncGrad)"
              strokeWidth="2"
              fill="none"
              strokeDasharray="6 6"
              strokeLinecap="round"
            >
              <animate attributeName="stroke-dashoffset" from="0" to="-24" dur="1.2s" repeatCount="indefinite" />
            </path>
            <circle id="syncDot" r="4" fill="#a3e635">
              <animateMotion id="syncMotion" dur="2.4s" repeatCount="indefinite" rotate="auto">
                <mpath href="#syncPath" />
              </animateMotion>
            </circle>
          </svg>

          {/* COACH WINDOW */}
          <div className="window coach">
            <div className="chrome">
              <div className="lights">
                <span />
                <span />
                <span />
              </div>
              <div className="url">
                <span className="url-pill">trainease.com.ar/coach</span>
              </div>
              <div style={{ width: 56 }} />
            </div>

            <div className="coach-body">
              <div className="coach-head">
                <div>
                  <div className="coach-title">Hipertrofia · Semana 3</div>
                  <div className="coach-sub">LUNES · EMPUJE PESADO</div>
                </div>
                <div className="live-pill">
                  <span className="dot" />
                  EN VIVO · 2
                </div>
              </div>

              <div className="coach-grid">
                <div className="coach-col">
                  <div className="coach-col-label">PLAN DE LA SESIÓN</div>
                  <div className="wo-row">
                    <div className="wo-bar" />
                    <div className="wo-info">
                      <div className="wo-name">Sentadilla trasera</div>
                      <div className="wo-meta">4 × 6 · RPE 8</div>
                    </div>
                    <span className="wo-pill">140kg</span>
                  </div>
                  <div className="wo-row">
                    <div className="wo-bar alt-1" />
                    <div className="wo-info">
                      <div className="wo-name">Press banca</div>
                      <div className="wo-meta">5 × 5 · RPE 7.5</div>
                    </div>
                    <span className="wo-pill">95kg</span>
                  </div>
                  <div className="wo-row">
                    <div className="wo-bar alt-2" />
                    <div className="wo-info">
                      <div className="wo-name">Remo con barra</div>
                      <div className="wo-meta">4 × 8 · RPE 7</div>
                    </div>
                    <span className="wo-pill">80kg</span>
                  </div>
                  <div className="wo-row">
                    <div className="wo-bar alt-3" />
                    <div className="wo-info">
                      <div className="wo-name">Dominadas lastradas</div>
                      <div className="wo-meta">3 × 6 · +10kg</div>
                    </div>
                    <span className="wo-pill">BW</span>
                  </div>
                  <div className="wo-row">
                    <div className="wo-bar alt-1" />
                    <div className="wo-info">
                      <div className="wo-name">Curl martillo</div>
                      <div className="wo-meta">3 × 12 · Tempo 3-0-1</div>
                    </div>
                    <span className="wo-pill">14kg</span>
                  </div>
                </div>

                <div className="coach-col">
                  <div className="coach-col-label">ATLETAS HOY</div>
                  <div className="at-row live">
                    <div className="at-av" style={{ background: 'linear-gradient(135deg,#a3e635,#65a30d)' }}>
                      MS
                    </div>
                    <div className="at-info">
                      <div className="at-name">Martina S.</div>
                      <div className="at-status">
                        <span className="pulse" />
                        Entrenando · set 3/4
                      </div>
                    </div>
                  </div>
                  <div className="at-row">
                    <div className="at-av" style={{ background: 'linear-gradient(135deg,#3b82f6,#1d4ed8)' }}>
                      LP
                    </div>
                    <div className="at-info">
                      <div className="at-name">Lucas P.</div>
                      <div className="at-status">Completó · 18 min</div>
                    </div>
                  </div>
                  <div className="at-row">
                    <div className="at-av" style={{ background: 'linear-gradient(135deg,#f97316,#c2410c)' }}>
                      RK
                    </div>
                    <div className="at-info">
                      <div className="at-name">Rocío K.</div>
                      <div className="at-status">Programado · 16:00</div>
                    </div>
                  </div>
                  <div className="at-row">
                    <div className="at-av" style={{ background: 'linear-gradient(135deg,#22c55e,#15803d)' }}>
                      DG
                    </div>
                    <div className="at-info">
                      <div className="at-name">Diego G.</div>
                      <div className="at-status">Completó · ayer</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ATHLETE WINDOW */}
          <div className="window athlete">
            <div className="chrome">
              <div className="lights">
                <span />
                <span />
                <span />
              </div>
              <div className="url">
                <span className="url-pill">trainease.com.ar/sesion</span>
              </div>
              <div style={{ width: 56 }} />
            </div>

            <div className="ath-body">
              <div className="ath-head">
                <div>
                  <div className="ath-name">EJERCICIO 1 / 5 · MARTINA S.</div>
                  <div className="ath-title">Sentadilla trasera</div>
                </div>
                <span className="rpe-pill">RPE 8</span>
              </div>

              <div className="set-display">
                <div className="set-block">
                  <div className="label">SET ACTUAL</div>
                  <div className="big" id="setCounter">
                    3<span className="of"> / 4</span>
                  </div>
                </div>
                <div className="set-block right">
                  <div className="label">OBJETIVO</div>
                  <div className="big">6 reps</div>
                  <div className="sub">@ 140 kg</div>
                </div>
              </div>

              <div className="set-list" id="setList">
                <div className="set-item done">
                  <div className="set-check">
                    <svg width="11" height="11" viewBox="0 0 11 11">
                      <path
                        d="M2 5.5L5 8.5L9 3"
                        stroke="#0a0a0a"
                        strokeWidth="2"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div className="set-label">Set 1</div>
                  <div className="set-vals">6 × 140kg</div>
                </div>
                <div className="set-item done">
                  <div className="set-check">
                    <svg width="11" height="11" viewBox="0 0 11 11">
                      <path
                        d="M2 5.5L5 8.5L9 3"
                        stroke="#0a0a0a"
                        strokeWidth="2"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div className="set-label">Set 2</div>
                  <div className="set-vals">6 × 140kg</div>
                </div>
                <div className="set-item active">
                  <div className="set-check">
                    <svg width="11" height="11" viewBox="0 0 11 11">
                      <path
                        d="M2 5.5L5 8.5L9 3"
                        stroke="#0a0a0a"
                        strokeWidth="2"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div className="set-label">Set 3 · en curso</div>
                  <div className="set-vals">— × 140kg</div>
                </div>
                <div className="set-item">
                  <div className="set-check">
                    <svg width="11" height="11" viewBox="0 0 11 11">
                      <path
                        d="M2 5.5L5 8.5L9 3"
                        stroke="#0a0a0a"
                        strokeWidth="2"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div className="set-label">Set 4</div>
                  <div className="set-vals">— × 140kg</div>
                </div>
              </div>

              <button className="ath-cta" type="button" tabIndex={-1}>
                Marcar set ✓
              </button>
            </div>
          </div>

          <div className="sync-badge" id="syncBadge">
            <div className="icon" />
            <div>
              <div className="label">SINCRONIZADO</div>
              <div className="value">en vivo</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
